import { useEffect, useState } from "react";
import { Modal } from "flowbite-react";
import ComboBoxFormik from "../../../common/components/form/ComboBoxFormik";
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../common/components/form/InputFormik";
import TextareaFormik from "../../renters/components/forms/TextareaFormik";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { maskLicensePlate } from "../../../common/helpers/mask.helpers";
import { IMaintenanceSchedule, MaintenanceScheduleStatus, MaintenanceScheduleType } from "../../../types/maintenanceSchedule.type";
import { updateMaintenanceSchedule } from "../../../common/services/maintenance-schedule.api";
import TimeSlotSelector from "./TimeSlotSelector";

export type Props = {
	maintenanceSchedule?: IMaintenanceSchedule;
	onClose: () => void;
	onSave: (maintenanceSchedule: IMaintenanceSchedule) => void;
};

const InsuranceClaimSchema = Yup.object().shape({
	licensePlate: Yup.string().required('Placa é obrigatória'),
	status: Yup.string().required('Status é obrigatório'),
	scheduledDate: Yup.date().required('Data é obrigatória'),
	scheduledTime: Yup.string().required('Hora é obrigatória'),
});

export function ModalEditMaintenanceSchedules(props: Props) {
	const [, setIsLoading] = useState(false);
	const { notifyError } = useToastContext();
	const [originalDate, setOriginalDate] = useState('');
	const [showTimeSelector, setShowTimeSelector] = useState(false);
	const maintenanceScheduleId = props.maintenanceSchedule?._id || '';


	const initialValues = (() => {
		if (!props.maintenanceSchedule) return {
			_id: maintenanceScheduleId || '',
			licensePlate: '',
			typeReview: '',
			companyId: '',
			author: '',
			renterName: '',
			renterPhone: '',
			renterVehicle: '',
			scheduledDate: '',
			scheduledTime: '',
			status: '',
			notes: '',
		};

		const formattedSchedule = JSON.parse(JSON.stringify(props.maintenanceSchedule));

		if (formattedSchedule.scheduledDate &&
			typeof formattedSchedule.scheduledDate === 'string' &&
			formattedSchedule.scheduledDate.includes('T')) {
			formattedSchedule.scheduledDate = formattedSchedule.scheduledDate.split('T')[0];
		}

		return formattedSchedule;
	})();

	useEffect(() => {
		if (props.maintenanceSchedule?.scheduledDate) {
			const date = typeof props.maintenanceSchedule.scheduledDate === 'string'
				? props.maintenanceSchedule.scheduledDate.split('T')[0]
				: '';
			setOriginalDate(date);
		}
	}, [props.maintenanceSchedule]);

	const handleSubmit = async (values) => {
		setIsLoading(true);
		try {
			const timeMatch = values.scheduledTime.match(/(\d{2}:\d{2})/);
			const formattedTime = timeMatch ? timeMatch[0] : values.scheduledTime;

			const formattedValues: IMaintenanceSchedule = {
				...values,
				scheduledTime: formattedTime
			};

			const { ...restWhit__v } = formattedValues;

			const result = await updateMaintenanceSchedule(restWhit__v);

			if (!result.ok)
				return notifyError(result.message || "Erro ao atualizar agendamento de manutenção");

			props.onSave(result.data);

			props.onClose();
		} catch (error) {
			console.error("Erro ao salvar o agendamento:", error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Modal
			className='relative text-black'
			show={true}
			onClose={() => props.onClose()}
			size="7x"
			theme={{
				content: {
					base: "relative h-full w-full p-4 md:h-auto",
					inner: "relative rounded-lg bg-white shadow dark:bg-gray-700 max-w-[90%] max-h-[80vh] mx-auto overflow-hidden flex flex-col"
				}
			}}
		>
			<Modal.Header className='text-black'>
				<h2 className='text-black'>
					{initialValues._id ?
						`Agendamento: ${initialValues.renterName ?
							initialValues.renterName.split(' ').map(name =>
								name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
							).join(' ') : 'Desconhecido'
						}` : "Editar Agendamento de Manutenção"}
				</h2>
			</Modal.Header>
			<Modal.Body className="min-h-[509px]">
				{/* Envolva todo o conteúdo do formulário com Formik */}
				<Formik
					initialValues={initialValues}
					validationSchema={InsuranceClaimSchema}
					onSubmit={handleSubmit}
				>
					{({ isSubmitting, values }) => (
						<Form>
							<div className="space-y-4 border rounded-lg p-4 bg-gray-50">
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
									<ComboBoxFormik
										label="Status:"
										name="status"
										value={values.status || ''}
										options={[
											{ value: "", label: "-" },
											{ value: MaintenanceScheduleStatus.Scheduled, label: "Agendado" },
											{ value: MaintenanceScheduleStatus.Completed, label: "Completado" },
											{ value: MaintenanceScheduleStatus.Canceled, label: "Cancelado" },
										]}
										disabled={true}
									/>

									<InputFormik
										label="Data:"
										name="scheduledDate"
										type="date"
										min={new Date().toISOString().split('T')[0]}
										value={
											typeof values.scheduledDate === 'object' && values.scheduledDate instanceof Date
												? values.scheduledDate.toISOString().split('T')[0]
												: typeof values.scheduledDate === 'string' && values.scheduledDate.includes('T')
													? values.scheduledDate.split('T')[0]
													: values.scheduledDate || ''
										}
										onChange={(e) => {
											if (e.target.value !== originalDate) {
												setShowTimeSelector(true);
											} else {
												setShowTimeSelector(false);
											}
										}}
										required={true}
									/>

									{showTimeSelector ? (
										<TimeSlotSelector />
									) : (
										<InputFormik
											label="Horário:"
											name="scheduledTime"
											value={values.scheduledTime || ''}
											type="time"
											required={true}
											readonly={true}
										/>
									)}

									<InputFormik
										label="Placa:"
										value={values.licensePlate || ''}
										name="licensePlate"
										readonly={true}
										mask={maskLicensePlate}
									/>

									<InputFormik
										label="Nome do cliente:"
										value={values.renterName || ''}
										name="renterName"
										readonly={true}
									/>

									<InputFormik
										label="Telefone do cliente:"
										value={values.renterPhone || ''}
										name="renterPhone"
										readonly={true}
									/>

									<ComboBoxFormik
										label="Tipo de agendamento:"
										name="typeReview"
										value={values.typeReview || ''}
										options={[
											{ value: MaintenanceScheduleType.Preventive, label: "Revisão Preventiva" },
											{ value: MaintenanceScheduleType.Corrective, label: "Revisão Corretiva" },
											{ value: MaintenanceScheduleType.Other, label: "Outros" },
										]}
										required={true}
									/>
								</div>

								{/* Observações */}
								<div className="mt-4">
									<label htmlFor="notes" className="block mb-2 text-sm font-medium">
										Observações:
									</label>
									<TextareaFormik
										label=""
										name="notes"
										className="w-full min-h-[150px] rounded-md shadow-sm p-3 border border-gray-300 focus:ring focus:ring-red-400"
										placeholder="Descreva informações relevantes..."
									/>
								</div>

								{/* Botão de ação */}
								<div className="flex justify-end pt-4">
									<ButtonWithLoader
										type="submit"
										isLoading={isSubmitting}
										disabled={isSubmitting}
									>
										Salvar
									</ButtonWithLoader>
								</div>
							</div>

						</Form>
					)}
				</Formik>
			</Modal.Body>
		</Modal>
	);
}